import { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import AuthInput from '../components/AuthInput';
import { BrandHeader } from '../components/AppHeader';
import GradientBackground from '../components/GradientBackground';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { isLoggedIn, parentFirebaseLogin, parentLogin } from '../api/auth';
import { authErrorMessage, validateLoginIdentifier, validatePassword } from '../utils/authValidation';
import SocialLoginButtons from '../components/SocialLoginButtons';
import { SocialProvider, signInWithFirebaseProvider } from '../services/firebaseAuth';

type Props = NativeStackScreenProps<RootStackParamList, 'Login'>;

export default function LoginScreen({ navigation }: Props) {
  const [loginValue, setLoginValue] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isLoggedIn()) {
      navigation.reset({ index: 0, routes: [{ name: 'Home' }] });
    }
  }, [navigation]);

  const goNext = (isNewUser?: boolean, profileCount = 0) => {
    if (isNewUser || profileCount === 0) {
      navigation.reset({ index: 0, routes: [{ name: 'CreateChildProfile' }] });
      return;
    }

    navigation.reset({ index: 0, routes: [{ name: 'ProfileSelect' }] });
  };

  const handleContinue = async () => {
    const identifier = loginValue.trim();
    const identifierError = validateLoginIdentifier(identifier);
    const passwordError = validatePassword(password);

    if (identifierError || passwordError) {
      setFormError(identifierError || passwordError || '');
      return;
    }

    setFormError('');
    setLoading(true);
    try {
      const loginData = await parentLogin(identifier, password);
      goNext(loginData.is_new_user, loginData.profiles?.length ?? 0);
    } catch (error) {
      setFormError(authErrorMessage(error, 'Vui lòng kiểm tra email/số điện thoại và mật khẩu.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: SocialProvider) => {
    setFormError('');
    setLoading(true);
    try {
      const idToken = await signInWithFirebaseProvider(provider);
      const loginData = await parentFirebaseLogin(idToken);
      goNext(loginData.is_new_user, loginData.profiles?.length ?? 0);
    } catch (error) {
      setFormError(authErrorMessage(error, 'Không thể đăng nhập bằng mạng xã hội. Vui lòng thử lại.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.screen}>
      <GradientBackground fullHeight />
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <View style={styles.topRow}>
          <View style={styles.topTextBlock}>
            <Text style={styles.heading}>Tài khoản phụ huynh</Text>
            <Text style={styles.subheading}>Đăng nhập để quản lý hồ sơ nghe riêng của bé.</Text>
          </View>
          <Pressable
            onPress={() => navigation.reset({ index: 0, routes: [{ name: 'Home' }] })}
            style={styles.homeButton}
            hitSlop={10}
          >
            <Text style={styles.homeButtonText}>Trang chủ</Text>
          </Pressable>
        </View>

        <View style={styles.logoHeader}>
          <BrandHeader />
        </View>

        <View style={styles.authBox}>
          <AuthInput
            label="Email hoặc số điện thoại"
            placeholder="name@example.com hoặc 090..."
            value={loginValue}
            onChangeText={(value) => {
              setLoginValue(value);
              if (formError) setFormError('');
            }}
          />
          <AuthInput
            label="Mật khẩu"
            placeholder="Nhập mật khẩu"
            secureTextEntry
            value={password}
            onChangeText={(value) => {
              setPassword(value);
              if (formError) setFormError('');
            }}
          />
          <Pressable onPress={() => navigation.navigate('ForgotPassword')} hitSlop={10}>
            <Text style={styles.forgotLink}>Quên mật khẩu?</Text>
          </Pressable>
          {formError ? <Text style={styles.errorText}>{formError}</Text> : null}
          <Pressable style={styles.mainButton} onPress={handleContinue} disabled={loading}>
            <Text style={styles.mainButtonText}>{loading ? 'Đang xử lý...' : 'Tiếp tục'}</Text>
          </Pressable>
          <SocialLoginButtons disabled={loading} onPress={handleSocialLogin} />

          <View style={styles.signupRow}>
            <Text style={styles.signupText}>Chưa có tài khoản?</Text>
            <Pressable onPress={() => navigation.navigate('Signup')} hitSlop={10}>
              <Text style={styles.signupLink}>Đăng ký</Text>
            </Pressable>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'rgb(242, 249, 253)',
  },
  container: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingTop: 18,
    paddingBottom: 34,
  },
  topRow: {
    minHeight: 70,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 6,
  },
  topTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  heading: {
    color: colors.buttonText,
    fontSize: 25,
    fontWeight: '900',
    lineHeight: 31,
  },
  subheading: {
    color: colors.buttonText,
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 18,
    marginTop: 6,
  },
  homeButton: {
    minHeight: 36,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.78)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
    marginTop: 2,
  },
  homeButtonText: {
    color: colors.textPrimary,
    fontSize: 13,
    fontWeight: '900',
  },
  logoHeader: {
    marginBottom: 2,
  },
  authBox: {
    borderRadius: 26,
    backgroundColor: 'rgba(255,255,255,0.92)',
    padding: 18,
  },
  forgotLink: {
    color: colors.button,
    textAlign: 'right',
    marginTop: -4,
    marginBottom: 14,
    fontSize: 13,
    fontWeight: '900',
  },
  errorText: {
    color: colors.danger,
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 18,
    marginBottom: 14,
  },
  mainButton: {
    backgroundColor: colors.button,
    borderRadius: 18,
    height: 50,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
  },
  mainButtonText: {
    color: colors.buttonText,
    fontWeight: '900',
    fontSize: 14,
  },
  signupRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 5,
    marginTop: 18,
  },
  signupText: {
    color: colors.textSecondary,
    fontSize: 14,
    fontWeight: '700',
  },
  signupLink: {
    color: colors.textSecondary,
    fontSize: 14,
    fontWeight: '900',
  },
});
